/**
 * Operator Progress Hook
 *
 * TanStack Query hook for the operator progress endpoint.
 * Follows the same patterns as useOperatorGoals.ts.
 *
 * - useOperatorProgress(offset?) -- fetch progress data with pagination
 *
 * Types are defined inline since these are read-only response types
 * that don't need to be in @guild/shared.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/hooks/useApi';

// ============================================
// TYPES (inline -- read-only response types)
// ============================================

export interface ProgressSummary {
  assessmentCount: number;
  prevAssessmentCount: number;
  trainingCompleted: number;
  newBadges: number;
  scoreChange: number | null;
  scoreTrend: 'improving' | 'steady' | 'declining' | null;
  currentLevel: string | null;
}

export interface RadarData {
  current: { compliance: number; efficiency: number; safety: number };
  previous: { compliance: number; efficiency: number; safety: number } | null;
}

export interface Achievement {
  type: string;
  title: string;
  earnedAt: string;
  goalTitle?: string;
}

export interface TimelineEvent {
  id: string;
  type: 'assessment' | 'training' | 'achievement' | 'quiz_completed' | 'sequencer_completed';
  date: string;
  // Assessment fields
  sopTitle?: string;
  taskFocus?: string;
  zoneName?: string;
  aiSummary?: string;
  trend?: 'improving' | 'steady' | 'declining' | 'first';
  managerNote?: string;
  managerName?: string;
  // Training fields
  materialTitle?: string;
  sopName?: string;
  // Achievement fields
  achievementType?: string;
  achievementTitle?: string;
  // Practice fields (quiz_completed / sequencer_completed)
  practiceScore?: string;
}

export interface ProgressPagination {
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface OperatorProgressData {
  summary: ProgressSummary;
  radar: RadarData;
  achievements: Achievement[];
  timeline: TimelineEvent[];
  pagination: ProgressPagination;
}

// ============================================
// QUERY KEYS
// ============================================

export const operatorProgressKeys = {
  all: ['operator-progress'] as const,
  data: (offset: number) => [...operatorProgressKeys.all, 'data', offset] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch operator progress data with pagination.
 *
 * staleTime: 5 min -- progress doesn't change frequently between shift checks.
 * placeholderData: keeps previous data during load-more to avoid flash-of-empty.
 */
export function useOperatorProgress(offset = 0) {
  const api = useApi();

  return useQuery({
    queryKey: operatorProgressKeys.data(offset),
    queryFn: () =>
      api.get<OperatorProgressData>(`/api/operator/progress?offset=${offset}`),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });
}
